import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";

const STUDIO_PASSWORD = "Catalyser27";
const UNLOCK_KEY = "operon_sprint_studio_unlocked";

/**
 * SprintStudio — password-gated host for the standalone Strategy Sprint Studio.
 * The actual workshop lives as a self-contained HTML/CSS/JS file in /public
 * and runs inside an iframe so all local-first behaviour (recording, backups,
 * localStorage per-browser case files) works exactly as designed.
 */
export default function SprintStudio() {
  const navigate = useNavigate();
  const [unlocked, setUnlocked] = useState(() => {
    try { return sessionStorage.getItem(UNLOCK_KEY) === "1"; } catch { return false; }
  });
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = (e) => {
    e.preventDefault();
    if (pwd.trim() === STUDIO_PASSWORD) {
      try { sessionStorage.setItem(UNLOCK_KEY, "1"); } catch { /* ignore */ }
      setUnlocked(true);
      setError("");
    } else {
      setError("Incorrect password. Please check and try again.");
    }
  };

  if (unlocked) {
    return (
      <div className="fixed inset-0 bg-[#f4f7f7] flex flex-col" data-testid="sprint-studio-frame-wrap">
        <div className="flex items-center justify-between px-4 py-2 bg-[#0b2836] text-white border-b border-[#1b4051]">
          <button
            onClick={() => navigate("/sprint")}
            className="flex items-center gap-2 text-sm text-[#cddce1] hover:text-white transition-colors"
            data-testid="sprint-studio-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Sprint</span>
          </button>
          <span className="text-[10px] tracking-[0.2em] font-semibold text-[#8dd9c9] uppercase">
            OPERON · Sprint Studio
          </span>
          <button
            onClick={() => {
              try { sessionStorage.removeItem(UNLOCK_KEY); } catch { /* ignore */ }
              setUnlocked(false);
              setPwd("");
            }}
            className="text-xs text-[#cddce1] hover:text-white transition-colors"
            data-testid="sprint-studio-lock-btn"
          >
            Lock
          </button>
        </div>
        <iframe
          src="/sprint-studio.html"
          title="Operon Strategy Sprint Studio"
          className="flex-1 w-full border-0"
          data-testid="sprint-studio-iframe"
          allow="microphone; clipboard-read; clipboard-write"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060B1C] text-[#E9EEF9] font-sans flex flex-col" data-testid="sprint-studio-gate">
      <div className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute top-[-10%] left-[-10%] w-[45rem] h-[45rem] rounded-full bg-emerald-500/10 blur-[120px]" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[42rem] h-[42rem] rounded-full bg-[#3E7AD6]/10 blur-[120px]" />
      </div>

      <nav className="border-b border-[#243768]/50 bg-[#0B1735]/60 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <button
            onClick={() => navigate("/sprint")}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
            data-testid="sprint-studio-nav-back"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Sprint</span>
          </button>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <form
          onSubmit={submit}
          className="w-full max-w-md rounded-2xl border border-[#243768] bg-[#0B1735]/70 backdrop-blur-md p-8 shadow-2xl"
          data-testid="sprint-studio-password-form"
        >
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Lock className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
          <div className="text-[10px] font-mono text-emerald-400 tracking-[0.3em] uppercase text-center mb-3">
            Protected workspace
          </div>
          <h1 className="text-2xl font-extrabold text-white text-center tracking-tight mb-2">
            Strategy Sprint Studio
          </h1>
          <p className="text-sm text-[#9DB0D6] text-center leading-relaxed mb-6">
            Enter the Studio password to open the 150-minute diagnostic workshop.
          </p>

          <label className="block text-xs font-semibold text-[#9DB0D6] mb-2" htmlFor="studio-pwd">
            Password
          </label>
          <input
            id="studio-pwd"
            type="password"
            value={pwd}
            onChange={(e) => { setPwd(e.target.value); if (error) setError(""); }}
            placeholder="Enter access password"
            autoFocus
            className="w-full px-4 py-3 rounded-lg bg-[#060B1C] border border-[#243768] text-white placeholder:text-[#4B5D86] focus:outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/20 transition-colors"
            data-testid="sprint-studio-password-input"
          />
          {error && (
            <div className="mt-3 text-xs text-red-400" data-testid="sprint-studio-password-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="mt-6 w-full bg-emerald-500 hover:bg-emerald-400 text-[#060B1C] font-semibold rounded-full py-6 h-auto text-base"
            data-testid="sprint-studio-unlock-btn"
          >
            Open Studio
          </Button>

          <p className="mt-6 text-[11px] text-[#6C7EA6] text-center leading-relaxed">
            The Studio runs locally in your browser. Case data stays on this device until you export a backup.
          </p>
        </form>
      </div>
    </div>
  );
}
